import { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import last from 'lodash/last'

import { transformTextParamsData } from '@pages/Parameters/helpers'

import { GetTextParamsHandle } from '@components/TextSettings'

import { usePrompt, useYupValidationResolver } from '@hooks'
import { getColumnParametersFromArray, getNewLastIndexByData } from '@helpers'
import { MODEL_TYPE } from '@constants'
import { AutocompleteOption } from '@types'

import { GanttColumnForm, PageContext } from '@gantt/components/GanttCreateOrEdit'
import { resourceColumnFieldDefaultValue } from '@gantt/constants'

import { getGanttColumnFormSchema } from '@validations'

import { getGanttColumnDefaultValue } from '../helpers/getGanttColumnDefaultValue'

import { useHandlers } from './useHandlers'
import { useInputs } from './useInputs'

export const useAddGanttFieldDialog = () => {
  const {
    currentRow, //todo проработать тип
    onSetModalProperty,
    objectCodeResource: viewObjectCode,
    bindingValuesColumns,
    currentColumnsList,
    modalType,
  } = useContext(PageContext)

  const defaultValues: GanttColumnForm = getGanttColumnDefaultValue(
    getNewLastIndexByData({
      items: currentColumnsList,
      getterNumber: value => last(value?.code?.split('_')),
    })
  )

  const resolver = useYupValidationResolver(
    getGanttColumnFormSchema(currentColumnsList, modalType === 'edit')
  )

  const methods = useForm<GanttColumnForm>({
    defaultValues,
    resolver,
  })
  const columnHeaderSettingsRef = useRef<GetTextParamsHandle>(null)
  const columnTextSettingsRef = useRef<GetTextParamsHandle>(null)
  const [isLoading, setLoading] = useState(true)

  const {
    handleSubmit,
    reset,
    formState: { isDirty: isDirtyBasicParameters, errors },
    watch,
    control,
  } = methods

  const watchPinning = watch('pinning')
  const watchUseParameters = watch('useParameters')

  const {
    fields: fields,
    append: appendField,
    remove: removeField,
  } = useFieldArray({ control, name: 'fields' })

  const { headerProperties, textProperties } = getColumnParametersFromArray(
    currentRow?.parameters || []
  )

  const initialColumnHeaderStyles = useMemo(() => {
    const arr: any = headerProperties
      ? Object.entries(headerProperties).map(([key, value]) => ({ key, value }))
      : []

    return transformTextParamsData(arr)
  }, [headerProperties])

  const initialColumnTextStyles = useMemo(() => {
    const arr: any = textProperties
      ? Object.entries(textProperties).map(([key, value]) => ({ key, value, num: 1, id: 1 }))
      : []

    return transformTextParamsData(arr)
  }, [textProperties])

  const bindTypeOptions = useMemo(
    () => bindingValuesColumns?.map(({ id, code: name }) => ({ id, name })),
    [bindingValuesColumns]
  )

  const getFormatDate = (formatDate: AutocompleteOption<string | number> | null | undefined) => {
    if (!formatDate) return null

    const isObject = typeof formatDate === 'object'

    return {
      id: isObject ? formatDate.id : formatDate,
      label: isObject ? formatDate.label : formatDate,
    }
  }

  useEffect(() => {
    if (currentRow) {
      reset({
        ...currentRow,
        pinning: Boolean(currentRow.pinnedColumn),
        useParameters: Boolean(currentRow?.useLocalParameters),
      })

      setLoading(false)
    }
  }, [currentRow])

  useEffect(() => {
    onSetModalProperty?.(MODEL_TYPE.COLUMN)
  }, [])

  const { state, handlers } = useHandlers({
    watchUseParameters,
    isDirty: isDirtyBasicParameters,
    handleSubmit,
    viewObjectCode: viewObjectCode || '',
    columnHeaderSettingsRef,
    columnTextSettingsRef,
    initialColumnHeaderStyles,
    initialColumnTextStyles,
  })

  const handleAddField = () => {
    appendField(structuredClone(resourceColumnFieldDefaultValue))
  }

  const handleDeleteField = (index: number) => {
    removeField(index)
  }

  const { isTextParamsDirty, isShowResetConfirmModal } = state
  const {
    handleSave,
    handleTextExternalDirty,
    handleHeaderExternalDirty,
    handleSetValuesColumnStyles,
    handleClickReset,
    handleResetParams,
    handleSetShowResetConfirmModal,
  } = handlers

  const isDirty = isDirtyBasicParameters || isTextParamsDirty

  usePrompt({ when: isDirty })

  const { columnInputs, otherColumnInputs } = useInputs({
    watchPinning,
    currentRow,
  })

  return {
    isDirty,
    handleSave,
    isLoading,
    methods,
    columnInputs,
    otherColumnInputs,
    columnHeaderSettingsRef,
    columnTextSettingsRef,
    initialColumnHeaderStyles,
    initialColumnTextStyles,
    watchUseParameters,
    isShowResetConfirmModal,
    handleTextExternalDirty,
    handleHeaderExternalDirty,
    handleSetValuesColumnStyles,
    handleClickReset,
    handleResetParams,
    handleSetShowResetConfirmModal,
    bindTypeOptions,
    fields,
    handleAddField,
    handleDeleteField,
    errors,
  }
}
